import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import BrazilMap from '../components/BrazilMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contato = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      setFormData({ name: '', phone: '', email: '', message: '' });
    } catch (err) {
      setError('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-6xl font-bold mb-4 md:mb-6">FEEDBACK</h1>
          <p className="text-base md:text-2xl max-w-3xl mx-auto">
            Entre em contato conosco. Estamos prontos para ajudar!
          </p>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Form */}
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Envie sua mensagem</h2>
              {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                  Mensagem enviada com sucesso! Entraremos em contato em breve.
                </div>
              )}
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Nome*</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    placeholder="Seu nome"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Número de telefone*</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">E-mail*</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    placeholder="seu@email.com"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Digite sua mensagem</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    placeholder="Sua mensagem..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <span>Enviando...</span>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>ENTRE EM CONTATO CONOSCO</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Map & Info */}
            <div className="space-y-6">
              {/* Map Embedded */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <iframe
                  src="https://maps.google.com/maps?q=Av%20J%C3%B4%20Sato%201953%20Novo%20Tempo%20Vilhena%20RO&t=&z=16&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localização Grupo Sodin - Matriz Vilhena"
                ></iframe>
              </div>

              {/* Contact Info */}
              <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Informações de Contato</h3>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800">Endereço (Matriz)</p>
                    <p className="text-gray-600">Av. Jô Sato, 1953</p>
                    <p className="text-gray-600">Novo Tempo, Vilhena - RO</p>
                    <p className="text-gray-600">CEP 76982-131</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800">Telefone / WhatsApp</p>
                    <a 
                      href="https://api.whatsapp.com/send?phone=556933214548" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-700 font-semibold flex items-center space-x-2"
                    >
                      <span>(69) 3321-4548</span>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800">E-mail</p>
                    <p className="text-gray-600">contato@cultivaragricola.com.br</p>
                  </div>
                </div>
              </div>

              {/* Trabalhe Conosco */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl shadow-lg p-6 text-white">
                <h3 className="text-2xl font-bold mb-3">Trabalhe Conosco</h3>
                <p className="text-sm mb-4">Quer fazer parte da nossa equipe? Envie seu currículo e venha crescer com a gente!</p>
                <a 
                  href="/trabalhe-conosco"
                  className="inline-block bg-white text-green-600 hover:bg-gray-100 font-semibold px-6 py-2 rounded-lg transition-colors"
                >
                  ENVIAR CURRÍCULO
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gerentes das Filiais */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-3">Gerentes das Filiais</h2>
          <p className="text-gray-600 text-base md:text-lg text-center mb-10 md:mb-12 max-w-2xl mx-auto">
            Fale diretamente com o gerente da unidade da sua região
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">
            {/* Cards dos gerentes */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { city: 'Vilhena (Matriz)', state: 'RO', manager: 'Abel Cândido', phone: '(69) 99601-0407', phoneRaw: '5569996010407', email: 'abel@cultivaragricola.com' },
                { city: 'Cacoal', state: 'RO', manager: 'Luiz Augusto Catanio', phone: '(69) 99275-3333', phoneRaw: '5569992753333', email: 'gerencia.cacoal@cultivaragricola.com' },
                { city: 'Pontes e Lacerda', state: 'MT', manager: 'Clemilson Luiz', phone: '(65) 99600-8340', phoneRaw: '5565996008340', email: 'gerencia.ponteselacerda@cultivaragricola.com' },
                { city: 'São Miguel do Guaporé', state: 'RO', manager: 'Baltazar Fernandes', phone: '(69) 98447-4778', phoneRaw: '5569984474778', email: 'gerente.saomiguel@cultivaragricola.com' },
                { city: 'Ariquemes', state: 'RO', manager: 'Alexandre Fetsch', phone: '(69) 98461-7269', phoneRaw: '5569984617269', email: 'alexandre@cultivaragricola.com' },
                { city: 'Cerejeiras', state: 'RO', manager: 'Eliel Felipe Schmitz', phone: '(69) 99375-1423', phoneRaw: '5569993751423', email: 'eliel@cultivaragricola.com' },
              ].map((m) => (
                <div key={m.city} className="bg-gray-50 border-l-4 border-blue-700 rounded-lg shadow-sm hover:shadow-lg transition-shadow p-5">
                  <div className="flex items-start space-x-3 mb-3">
                    <MapPin className="w-6 h-6 text-blue-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-lg text-gray-800">{m.city}</h3>
                      <p className="text-sm text-gray-500">{m.state}</p>
                      <p className="text-sm font-semibold text-blue-700 mt-1" data-testid={`manager-name-${m.city.toLowerCase().replace(/\s+/g, '-')}`}>
                        {m.manager}
                      </p>
                    </div>
                  </div>
                  <a
                    href={`tel:+${m.phoneRaw}`}
                    className="flex items-center space-x-2 text-gray-700 hover:text-blue-700 font-medium text-sm mb-2"
                    data-testid={`manager-phone-${m.city.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span>{m.phone}</span>
                  </a>
                  <a
                    href={`mailto:${m.email}`}
                    className="flex items-center space-x-2 text-blue-700 hover:text-blue-800 font-medium text-sm break-all"
                  >
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span>{m.email}</span>
                  </a>
                </div>
              ))}
            </div>

            {/* Mapa do Brasil */}
            <div className="flex flex-col items-center lg:sticky lg:top-24">
              <p className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 text-center">
                Nossa região de atuação
              </p>
              <img
                src="https://customer-assets.emergentagent.com/job_f1f17df6-0a32-4d0a-b237-628c328d9963/artifacts/e9bsjon0_ChatGPT%20Image%2011%20de%20mai.%20de%202026%2C%2008_39_57.png"
                alt="Mapa do Brasil destacando Acre, Rondônia e Mato Grosso"
                className="w-full max-w-md object-contain"
                data-testid="region-map-img"
              />
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600">
                <span className="inline-block w-4 h-4 rounded-sm" style={{backgroundColor: '#0072D6'}}></span>
                <span>Acre, Rondônia e Mato Grosso</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cultivar Log - Transportadora (por último) */}
      <section className="py-12 md:py-16 bg-gray-50" data-testid="contato-logistica-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-3">Cultivar Log — Transportadora</h2>
          <p className="text-gray-600 text-base md:text-lg text-center mb-8 md:mb-10 max-w-2xl mx-auto">
            Precisa de transporte ou tem dúvidas sobre logística? Fale diretamente com o time da nossa transportadora.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            <div className="bg-white border-l-4 border-blue-700 rounded-lg shadow-sm hover:shadow-lg transition-shadow p-5" data-testid="contato-card-logistica">
              <div className="flex items-start space-x-3 mb-3">
                <MapPin className="w-6 h-6 text-blue-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-lg text-gray-800">Logística</h3>
                  <p className="text-sm text-gray-500">Atendimento operacional</p>
                </div>
              </div>
              <a
                href="https://wa.me/5569992388476"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-gray-700 hover:text-green-600 font-medium text-sm"
                data-testid="contato-tel-logistica"
              >
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>(69) 99238-8476</span>
              </a>
            </div>

            <div className="bg-white border-l-4 border-blue-700 rounded-lg shadow-sm hover:shadow-lg transition-shadow p-5" data-testid="contato-card-gerente-log">
              <div className="flex items-start space-x-3 mb-3">
                <MapPin className="w-6 h-6 text-blue-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-lg text-gray-800">Gerente da Transportadora</h3>
                  <p className="text-sm font-semibold text-blue-700 mt-1">João Gabriel</p>
                </div>
              </div>
              <a
                href="https://wa.me/5569999999997"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-gray-700 hover:text-green-600 font-medium text-sm"
                data-testid="contato-tel-joao-gabriel"
              >
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>(69) 99999-9997</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contato;Contato;